import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { MessageService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { Company } from '../company.model';
import { TenantAccessService } from '../tenant-access.service';
import { CompanyAccess } from '../tenant-access.model';
import { Customer } from '../../customer/customer.model';
import { CustomerService } from '../../customer/customer.service';
import { Role } from '../../customer/customer-role.model';
import { SessionStorageService } from '../../services/session-storage.service';

@Component({
    selector: 'app-company-group-tenant-access',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        MultiSelectModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        TooltipModule
    ],
    providers: [MessageService],
    template: `
        <p-toast></p-toast>

        <div class="tenant-access-shell">
            <div class="tenant-access-note">
                <i class="pi pi-shield"></i>
                <span>
                    Tenant administration is private. You can manage only users whose home tenant is
                    <strong>{{ company?.name || 'your company' }}</strong>. Each member company’s administrators
                    manage their own users and group-role assignments from their own portal. Group membership never
                    grants visibility of another tenant's users.
                </span>
            </div>

            <div class="tenant-access-form">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <input id="tenantCompany" class="w-full" [value]="company?.name || ''" readonly />
                        <label for="tenantCompany">Your tenant</label>
                    </p-iftalabel>
                </p-inputGroup>

                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select
                            id="tenantUser"
                            [options]="companyCustomerOptions"
                            [(ngModel)]="selectedCustomer"
                            optionLabel="label"
                            optionValue="value"
                            [filter]="true"
                            [showClear]="true"
                            appendTo="body"
                            styleClass="w-full"></p-select>
                        <label for="tenantUser">Customer</label>
                    </p-iftalabel>
                </p-inputGroup>

                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-multiSelect
                            inputId="tenantRoles"
                            [options]="companyRoleOptions"
                            [(ngModel)]="roleIds"
                            optionLabel="label"
                            optionValue="value"
                            appendTo="body"
                            styleClass="w-full"></p-multiSelect>
                        <label for="tenantRoles">Tenant roles</label>
                    </p-iftalabel>
                </p-inputGroup>

                <div class="tenant-access-actions">
                    <p-button
                        type="button"
                        icon="pi pi-save"
                        [label]="editingAccess ? 'Update access' : 'Grant access'"
                        [disabled]="!canSave()"
                        (click)="saveAccess()"></p-button>
                    <p-button
                        *ngIf="editingAccess"
                        type="button"
                        icon="pi pi-times"
                        label="Cancel"
                        severity="secondary"
                        [outlined]="true"
                        (click)="resetForm()"></p-button>
                </div>
            </div>

            <p-table [value]="companyUsers" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '58rem' }">
                <ng-template pTemplate="header">
                    <tr>
                        <th>User</th>
                        <th>Home tenant</th>
                        <th>Status</th>
                        <th>Tenant roles</th>
                        <th class="text-right">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>
                            <div class="user-cell">
                                <strong>{{ row.customerName || ('Customer ' + row.customerId) }}</strong>
                                <span>{{ row.customerEmail || 'No email address' }}</span>
                            </div>
                        </td>
                        <td>{{ company?.name || 'Your tenant' }}</td>
                        <td><p-tag [value]="row.active ? 'Active' : 'Inactive'" [severity]="row.active ? 'success' : 'secondary'"></p-tag></td>
                        <td><p-tag *ngFor="let role of row.roles" [value]="role.roleName" severity="info"></p-tag></td>
                        <td class="text-right">
                            <button
                                pButton
                                type="button"
                                icon="pi pi-pencil"
                                pTooltip="Edit tenant access"
                                class="p-button-rounded p-button-text p-button-secondary"
                                (click)="editAccess(row)"></button>
                            <button
                                pButton
                                type="button"
                                icon="pi pi-trash"
                                pTooltip="Revoke tenant access"
                                class="p-button-rounded p-button-text p-button-danger"
                                (click)="revokeAccess(row)"></button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="5" class="text-center p-4">No direct user assignments in your tenant.</td></tr>
                </ng-template>
            </p-table>
        </div>
    `,
    styles: [`
        .tenant-access-shell { display: grid; gap: 1rem; }
        .tenant-access-note { display: flex; gap: .55rem; align-items: flex-start; padding: .8rem; border-radius: .5rem; background: color-mix(in srgb, var(--primary-color) 8%, transparent); color: var(--text-color-secondary); line-height: 1.4; }
        .tenant-access-form { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(220px, 1.2fr) minmax(220px, 1.2fr) auto; gap: .75rem; align-items: center; }
        .tenant-access-actions { display: inline-flex; gap: .5rem; align-items: center; flex-wrap: wrap; }
        .user-cell { display: grid; gap: .15rem; line-height: 1.25; }
        .user-cell span { color: var(--text-color-secondary); font-size: .82rem; }
        :host ::ng-deep p-iftalabel, :host ::ng-deep .p-iftalabel, :host ::ng-deep .p-select, :host ::ng-deep .p-multiselect { width: 100%; }
        @media (max-width: 1100px) { .tenant-access-form { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 700px) { .tenant-access-form { grid-template-columns: 1fr; } }
    `]
})
export class CompanyGroupTenantAccessComponent implements OnInit, OnChanges {
    /**
     * This is intentionally the signed-in user's own company, never the list
     * of companies in the group. Tenant user administration is not a group
     * owner capability.
     */
    @Input() company?: Company;

    companyId?: number;
    companyUsers: CompanyAccess[] = [];
    companyCustomers: Customer[] = [];
    roles: Role[] = [];
    roleIds: number[] = [];
    selectedCustomer: Customer | null = null;
    editingAccess: CompanyAccess | null = null;

    constructor(
        private readonly tenantAccessService: TenantAccessService,
        private readonly customerService: CustomerService,
        private readonly sessionStorageService: SessionStorageService,
        private readonly messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.customerService.getAllRoles('ANY').subscribe({
            next: roles => this.roles = (roles ?? []).filter(role => !!role.roleId),
            error: () => this.roles = []
        });
        this.loadOwnCompanyData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['company']) {
            return;
        }
        this.loadOwnCompanyData();
    }

    get companyCustomerOptions() {
        return this.companyCustomers.map(customer => ({
            label: this.customerLabel(customer),
            value: customer
        }));
    }

    get companyRoleOptions() {
        return this.roles
            .filter(role => this.isCompanyRole(role))
            .map(role => ({ label: role.roleName, value: role.roleId }));
    }

    canSave(): boolean {
        return !!this.companyId && !!this.selectedCustomer?.id && this.roleIds.length > 0;
    }

    saveAccess(): void {
        if (!this.canSave() || !this.companyId || !this.selectedCustomer?.id) {
            return;
        }
        this.tenantAccessService.saveCompanyUser(this.companyId, {
            customerId: this.selectedCustomer.id,
            active: true,
            roleIds: this.roleIds,
            updatedBy: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.editingAccess ? 'Tenant access updated' : 'Tenant access granted',
                    detail: `${this.customerLabel(this.selectedCustomer!)} can now access ${this.company?.name || 'this tenant'}.`
                });
                this.resetForm();
                this.loadCompanyUsers();
            },
            error: error => this.messageService.add({
                severity: 'error',
                summary: 'Tenant access not saved',
                detail: error?.error?.message || 'Could not save direct tenant access.'
            })
        });
    }

    editAccess(access: CompanyAccess): void {
        if (!access?.customerId) {
            return;
        }
        this.editingAccess = access;
        this.selectedCustomer = {
            id: access.customerId,
            name: access.customerName,
            emailAddress: access.customerEmail
        } as Customer;
        this.roleIds = (access.roles ?? [])
            .map(role => role.roleId)
            .filter((roleId): roleId is number => typeof roleId === 'number');
    }

    revokeAccess(access: CompanyAccess): void {
        if (!this.companyId || !access?.customerId) {
            return;
        }
        const label = access.customerName || `Customer ${access.customerId}`;
        if (!window.confirm(`Revoke ${label}'s access to ${this.company?.name || 'this tenant'}?`)) {
            return;
        }
        this.tenantAccessService.deactivateCompanyUser(
            this.companyId,
            access.customerId,
            this.sessionStorageService.getCustomer()?.id
        ).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Tenant access revoked', detail: `${label} no longer has access to this tenant.` });
                if (this.editingAccess?.customerId === access.customerId) {
                    this.resetForm();
                }
                this.loadCompanyUsers();
            },
            error: error => this.messageService.add({
                severity: 'error',
                summary: 'Tenant access not revoked',
                detail: error?.error?.message || 'Could not revoke direct tenant access.'
            })
        });
    }

    resetForm(): void {
        this.selectedCustomer = null;
        this.roleIds = [];
        this.editingAccess = null;
    }

    customerLabel(customer: Customer): string {
        const name = customer.name?.trim() || `Customer ${customer.id}`;
        return customer.emailAddress ? `${name} (${customer.emailAddress})` : name;
    }

    private loadCompanyUsers(): void {
        if (!this.companyId) {
            this.companyUsers = [];
            return;
        }
        this.tenantAccessService.getCompanyUsers(this.companyId).subscribe({
            next: users => this.companyUsers = users ?? [],
            error: () => {
                this.companyUsers = [];
                this.messageService.add({ severity: 'error', summary: 'Tenant users unavailable', detail: 'Could not load the selected tenant users.' });
            }
        });
    }

    private loadOwnCompanyData(): void {
        const nextCompanyId = this.company?.id;
        if (!nextCompanyId) {
            this.companyId = undefined;
            this.companyUsers = [];
            this.companyCustomers = [];
            this.resetForm();
            return;
        }

        if (this.companyId !== nextCompanyId) {
            this.companyId = nextCompanyId;
            this.resetForm();
        }
        this.loadCompanyUsers();
        this.customerService.getCustomersByCompany(nextCompanyId).subscribe({
            next: customers => this.companyCustomers = (customers ?? [])
                .filter(customer => customer?.id && customer.company?.id === nextCompanyId),
            error: () => {
                this.companyCustomers = [];
                this.messageService.add({ severity: 'error', summary: 'Tenant users unavailable', detail: 'Could not load users in your tenant.' });
            }
        });
    }

    private isCompanyRole(role: Role): boolean {
        const scope = (role.roleScope || '').trim().toUpperCase();
        const hasPortalPermission = (role.permissions ?? []).some(permission =>
            (permission.permissionCode || '').trim().toLowerCase().startsWith('portal.')
        );
        return (!scope || scope === 'COMPANY') && !hasPortalPermission;
    }
}
